/**
 * Snapchat-style streak calculator
 * Tracks consecutive days with at least one scan
 */

import { ScanRecord } from '../services/scanService';

/**
 * Get today's date as YYYY-MM-DD in the user's local timezone
 */
export function getTodayLocalDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get yesterday's date as YYYY-MM-DD in the user's local timezone
 */
export function getYesterdayLocalDate(): string {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const year = yesterday.getFullYear();
  const month = String(yesterday.getMonth() + 1).padStart(2, '0');
  const day = String(yesterday.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get a date string in YYYY-MM-DD format from a Date object or timestamp string
 */
export function getLocalDateString(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Calculate the number of days between two dates (date strings in YYYY-MM-DD format)
 */
export function daysBetween(date1: string, date2: string): number {
  const d1 = new Date(date1 + 'T00:00:00');
  const d2 = new Date(date2 + 'T00:00:00');
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

export interface StreakData {
  streakCount: number;
  lastStreakDate: string; // YYYY-MM-DD format in user's local timezone
}

/**
 * Calculate streak from all scans (Snapchat-style)
 * Finds the longest consecutive sequence of days with at least one scan
 * The streak must include today or yesterday to be active
 * 
 * @param scans - Array of completed scans
 * @returns Streak data with count and last streak date
 */
export function calculateStreakFromScans(scans: ScanRecord[]): StreakData {
  if (!scans || scans.length === 0) {
    return {
      streakCount: 0,
      lastStreakDate: '',
    };
  }

  // Get unique dates (YYYY-MM-DD) from scans
  const scanDates = new Set<string>();
  scans.forEach(scan => {
    if (scan.status === 'complete') {
      const scanDate = getLocalDateString(scan.createdAt || scan.timestamp);
      scanDates.add(scanDate);
    }
  });

  if (scanDates.size === 0) {
    return {
      streakCount: 0,
      lastStreakDate: '',
    };
  }

  const today = getTodayLocalDate();
  const yesterday = getYesterdayLocalDate();

  // Check if there's a scan today or yesterday
  // If not, the streak is broken (must be active)
  const hasScanToday = scanDates.has(today);
  const hasScanYesterday = scanDates.has(yesterday);

  if (!hasScanToday && !hasScanYesterday) {
    // No active streak - last scan was more than 1 day ago
    return {
      streakCount: 0,
      lastStreakDate: '',
    };
  }

  // Start counting from today if there's a scan today, otherwise from yesterday
  let currentDate: Date;
  let streakCount = 0;
  let lastStreakDate: string;

  if (hasScanToday) {
    currentDate = new Date(today + 'T00:00:00');
    lastStreakDate = today;
    streakCount = 1;
  } else {
    // Has scan yesterday but not today - streak is still active (1 day)
    currentDate = new Date(yesterday + 'T00:00:00');
    lastStreakDate = yesterday;
    streakCount = 1;
  }

  // Work backwards day by day to find consecutive days
  while (true) {
    // Move to previous day
    currentDate.setDate(currentDate.getDate() - 1);
    const previousDateStr = getLocalDateString(currentDate);

    // Check if there's a scan on this day
    if (scanDates.has(previousDateStr)) {
      streakCount++;
    } else {
      // Gap found - streak ends here
      break;
    }
  }

  return {
    streakCount,
    lastStreakDate,
  };
}

/**
 * Update streak when a new scan is completed
 * This is a quick update function, but for accuracy, use calculateStreakFromScans
 * 
 * @param currentStreakData - Current streak data from user profile (can be undefined for new users)
 * @returns Updated streak data
 */
export function updateStreakOnScan(currentStreakData?: StreakData): StreakData {
  const today = getTodayLocalDate();
  
  // If user has no previous streak data
  if (!currentStreakData || !currentStreakData.lastStreakDate) {
    return {
      streakCount: 1,
      lastStreakDate: today,
    };
  }
  
  const lastStreakDate = currentStreakData.lastStreakDate;
  const yesterday = getYesterdayLocalDate();
  
  // If lastStreakDate is today (multiple scans in the same day)
  if (lastStreakDate === today) {
    // Do not change streakCount, just keep the same data
    return {
      streakCount: currentStreakData.streakCount,
      lastStreakDate: today,
    };
  }
  
  // If lastStreakDate is yesterday (consecutive day)
  if (lastStreakDate === yesterday) {
    return {
      streakCount: currentStreakData.streakCount + 1,
      lastStreakDate: today,
    };
  }
  
  // If there is a gap of 2 or more days with no scans
  const daysDiff = daysBetween(lastStreakDate, today);
  if (daysDiff >= 2) {
    // Reset the streak
    return {
      streakCount: 1,
      lastStreakDate: today,
    };
  }
  
  // Fallback: if somehow lastStreakDate is in the future (shouldn't happen), reset
  if (daysDiff < 0) {
    return {
      streakCount: 1,
      lastStreakDate: today,
    };
  }
  
  // Default fallback
  return {
    streakCount: 1,
    lastStreakDate: today,
  };
}

/**
 * Format streak count into a human-readable label
 */
export function formatStreakLabel(streakCount: number): string {
  if (streakCount === 0) {
    return '0 days';
  } else if (streakCount === 1) {
    return '1 day';
  } else if (streakCount < 7) {
    return `${streakCount} days`;
  } else if (streakCount < 30) {
    const weeks = Math.floor(streakCount / 7);
    return weeks === 1 ? '1 week' : `${weeks} weeks`;
  } else if (streakCount < 365) {
    const months = Math.floor(streakCount / 30);
    return months === 1 ? '1 month streak' : `${months} month streak`;
  } else {
    const years = Math.floor(streakCount / 365);
    const remainingDays = streakCount % 365;
    if (years === 1 && remainingDays === 0) {
      return '1 year streak';
    } else if (years === 1) {
      const months = Math.floor(remainingDays / 30);
      if (months > 0) {
        return `1 year ${months} month streak`;
      } else {
        return `1 year ${remainingDays} day streak`;
      }
    } else {
      const months = Math.floor(remainingDays / 30);
      if (months > 0) {
        return `${years} years ${months} month streak`;
      } else if (remainingDays > 0) {
        return `${years} years ${remainingDays} day streak`;
      } else {
        return `${years} year streak`;
      }
    }
  }
}
